import React, { useMemo } from 'react';
import {
  CellRendererProps,
  FlatList,
  FlatListProps,
  Platform,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
  ViewToken,
} from 'react-native';
import { HorizontalListHeader } from './horizontal-list-header';
import { ListOption } from './list-item';
import { useLayoutContext } from '@/contexts/layout-context';

export const LIST_MARGIN_HORIZONTAL_LARGE = 24;

// horizontal is always true. For vertical, use the List component
type ModifiedFlatListType<TListOption> = Pick<
  FlatListProps<TListOption>,
  Exclude<
    keyof FlatListProps<TListOption>,
    'horizontal' | 'onViewableItemsChanged'
  >
>;

type BaseProps<TListOption> = ModifiedFlatListType<TListOption> & {
  containerStyle?: StyleProp<ViewStyle>;
  HeaderImageComponent?: React.ReactNode;
  itemCount?: number;
  largeHeaderStyle?: StyleProp<ViewStyle>;
  largeListStyle?: StyleProp<ViewStyle>;
  limit: number;
  ListHeaderTagComponent?: React.ReactNode;
  listRef?: React.Ref<FlatList>;
  onLeftArrowPress?: () => void;
  onRightArrowPress?: () => void;
  onSeeAllPress?: () => void;
  title?: string;
};

export type Props<TListOption> = BaseProps<TListOption> &
  (
    | {
        itemWidth?: number;
        onViewableItemsChanged?: undefined;
      }
    | {
        itemWidth: number;
        onViewableItemsChanged: (props: { viewableItems: ViewToken[] }) => void;
      }
  );

const defaultKeyExtractor = ({ value }: ListOption) => String(value);

export const HorizontalList = <TListOption extends ListOption = ListOption>({
  containerStyle,
  data,
  HeaderImageComponent,
  itemCount,
  largeHeaderStyle,
  keyExtractor = defaultKeyExtractor,
  largeListStyle,
  limit,
  ListHeaderTagComponent,
  listRef,
  onLeftArrowPress,
  onRightArrowPress,
  onSeeAllPress,
  style,
  title,
  ...flatListProps
}: Props<TListOption>) => {
  const { isSmallLayout } = useLayoutContext();

  const CellRenderer = useMemo(() => {
    const itemWidth = flatListProps.itemWidth;

    // Implement a custom cell renderer on web to prevent onViewableItemsChanged
    // from firing for every item on first render
    // https://github.com/necolas/react-native-web/issues/1798
    return Platform.OS === 'web' && typeof itemWidth === 'number'
      ? <T extends ListOption>({ onLayout, ...rest }: CellRendererProps<T>) => (
          <View
            {...rest}
            onLayout={(e) => {
              // Check itemWidth to ensure the cell is rendered
              if (itemWidth > 0) {
                onLayout?.({
                  ...e,
                  nativeEvent: {
                    ...e.nativeEvent,
                    layout: {
                      ...e.nativeEvent.layout,
                      x: rest.index * itemWidth,
                    },
                  },
                });
              }
            }}
          />
        )
      : <T extends ListOption>(props: CellRendererProps<T>) => (
          <View {...props} />
        );
  }, [flatListProps.itemWidth]);

  const listData = (data || []) as TListOption[];
  return (
    <View style={[styles.base, containerStyle]}>
      {title ? (
        <HorizontalListHeader
          ImageComponent={HeaderImageComponent}
          itemCount={itemCount ?? listData.length}
          largeHeaderStyle={largeHeaderStyle}
          onLeftArrowPress={onLeftArrowPress}
          onRightArrowPress={onRightArrowPress}
          onSeeAllPress={onSeeAllPress}
          TagComponent={ListHeaderTagComponent}
          title={title}
        />
      ) : null}
      <FlatList
        CellRendererComponent={CellRenderer}
        data={listData.slice(0, limit)}
        decelerationRate="fast"
        horizontal
        keyExtractor={keyExtractor}
        pagingEnabled
        ref={listRef}
        showsHorizontalScrollIndicator={false}
        style={[isSmallLayout ? null : styles.listLarge, style, largeListStyle]}
        {...flatListProps}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  base: {
    paddingVertical: 8,
  },
  listLarge: {
    marginHorizontal: LIST_MARGIN_HORIZONTAL_LARGE,
  },
});
